import { api } from './api';
import type { ApiResponse } from './api';

/**
 * 일반 팝업 기제의 web 쪽 — 판정 조회 · 「확인」 기록
 * (줄기 `popup-and-announcement-surface` P2 · 결정 2026-10-15 §B).
 *
 * ⚠️ **`lib/api.ts`에 넣지 않는다** — 그 파일은 공용 axios 인스턴스·토큰 관리의 자리이고,
 * 도메인별 호출은 `lib/feedback.ts`·`lib/notifications.ts`처럼 별 모듈이 관례다.
 *
 * ⚠️ **「오늘 하루 보지 않기」는 2026-10-26에 `packages/ui`로 옮겼다**(`popupHideToday.ts`).
 * 여기 남은 둘은 **계정 축**이라 api 클라이언트가 필요해 앱이 소유하고, 그쪽은 의존이
 * `window.localStorage` 하나라 공용 호스트와 함께 산다 — 그 가름의 근거는 그 모듈 JSDoc이
 * 정본이다. 「오늘 하루」를 다시 이 파일로 끌어오지 말 것(admin이 사본을 갖게 된다).
 */

/** `GET /popups/active` 한 건 — 서버 `ActivePopupView`와 같은 형상 */
export interface ActivePopup {
  id: string;
  /** null = 전사 */
  organizationId: string | null;
  title: string;
  body: string;
  /** ISO 8601. 「오늘 하루」 키와 무관하게 서버 봤음 비교의 기준 시각이다 */
  contentRevisedAt: string;
  /**
   * 재노출 — 화면은 **확인 버튼 문구**만 이것으로 가른다(`packages/ui`의 `PopupHostItem` JSDoc).
   *
   * ⚠️ **2026-10-29에 담기 시작했다** — 종전 이 자리는 「일부러 담지 않았다 — 화면이 그 값으로
   * 분기하지 않는다」였고 그 전제가 뒤집혔다(결정 ⓒ: 문구가 그 값으로 갈린다). **`string`으로 둔다** —
   * 손으로 열거하면 Prisma `PopupFrequency`의 두 번째 사본이 된다(그 경위가 종전 주석의 요지였다).
   */
  frequency: string;
  /** opt-out — 버튼 종류의 분모. 같은 이유로 `string`이고 해석은 공용 호스트가 한다 */
  optOut: string;
  /** 「전체 내역 보기」 라벨·주소 — 해석은 공용 호스트(`resolvePopupLinks`)가 한다 */
  linkLabel?: string | null;
  linkUrl?: string | null;
}

export async function fetchActivePopups(): Promise<ActivePopup[]> {
  const res = await api.get<ApiResponse<ActivePopup[]>>('/popups/active');
  return res.data.data ?? [];
}

/**
 * 「확인」(닫기) — 서버에 봤음을 남긴다. **계정 축**이라 기기가 바뀌어도 유지된다.
 *
 * `EVERY_VISIT` 팝업에도 보낸다 — 서버가 그 빈도에서는 봤음을 보지 않지만, 「언제 닫았나」가
 * 남아 있으면 재노출 정책을 되짚을 때 쓸 재료가 된다(결정 2026-10-14 ⓐ의 되짚을 조건이
 * 「같은 날 닫은 뒤에 추가된 항목이 있는 사용자 수」다).
 */
export async function markPopupSeen(popupId: string): Promise<void> {
  await api.post(`/popups/${popupId}/seen`);
}

/**
 * 「다시 보지 않기」 — 서버에 계정 단위로 남긴다(결정 2026-10-26 개정 Q2). 그 팝업은 본문이 바뀌어도
 * 이 계정에 다시 뜨지 않는다. 그 팝업이 그 선택지를 제공하지 않으면 서버가 400을 낸다(호스트가 삼킨다).
 *
 * ⚠️ body를 **`{}`로 보낸다** — `null`이면 일부 환경에서 CORS preflight가 깨진다
 * (`memory/feedback-axios-post-null-body-cors` · 형제 `lib/announcements.ts`의 `markAnnouncementSeen`).
 */
export async function dismissPopup(popupId: string): Promise<void> {
  await api.post(`/popups/${popupId}/dismiss`, {});
}
